/**
 * TrialLastCallModal — the final-24h popup (TRIAL_PLAN.md §8.7). Copy approved
 * by Randy (voice B, Oct 15 2026) and used verbatim.
 *
 * Shown once, only while the trial is genuinely about to convert — a cancelled
 * trial never sees it, because for them nothing becomes anything tomorrow.
 * The Partner Program line sits BELOW the buttons, outside the consent block,
 * and stays conditional: a trial user does not yet qualify (§8.7, D44).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTrial, ackTrial, setTrial, getProCatalog, flattenCatalog, money } from "@/lib/trial";
import TrialCreditNote from "@/components/TrialCreditNote";
import EarningsDisclaimer from "@/components/EarningsDisclaimer";
import CreatorProBuffetModal from "@/components/CreatorProBuffetModal";

export default function TrialLastCallModal() {
  const { trial } = useTrial();
  const [open, setOpen] = useState(false);
  const [buffet, setBuffet] = useState(false);
  const [used, setUsed] = useState([]);
  const [busy, setBusy] = useState(false);

  const due = !!trial?.live
    && !trial.cancel_at_period_end
    && !trial.lastcall_shown_at
    && Number(trial.hours_left || 99) <= 24;

  useEffect(() => { if (due) setOpen(true); }, [due]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    getProCatalog().then((cat) => {
      if (!alive) return;
      const leaves = flattenCatalog(cat);
      const keys = new Set([
        ...(trial?.prompted_skus || []),
        ...(trial?.committed_skus || []).map((r) => r.sku),
      ]);
      setUsed(leaves.filter((l) => keys.has(l.key)).map((l) => l.name));
    });
    return () => { alive = false; };
  }, [open, trial]);

  if (!trial?.live) return null;

  const close = async () => {
    setOpen(false);
    await ackTrial("lastcall");
  };

  const keepEverything = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trial/commit-bundle", {
        price_cents: trial.bundle_cents, screen: "lastcall",
      });
      setTrial(r.data?.trial);
      toast.success(`Everything is on tomorrow's invoice — ${money(trial.bundle_cents)}, one charge.`);
      await close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add the bundle.");
    } finally { setBusy(false); }
  };

  const toolList = used.length
    ? used.slice(0, 4).join(", ") + (used.length > 4 ? `, and ${used.length - 4} more` : "")
    : "the Creator Pro tools";

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent
          className="max-w-lg bg-gradient-to-br from-[#1C1B1A] to-[#4A2F2A] text-[#FDFCF8] border-0 overflow-y-auto top-[calc(env(safe-area-inset-top,0px)+1rem)] translate-y-0 max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2rem)] sm:top-1/2 sm:-translate-y-1/2 sm:max-h-[88vh]"
          data-testid="trial-lastcall"
        >
          <DialogTitle className="sr-only">Your trial ends tomorrow</DialogTitle>
          <DialogDescription className="sr-only">
            Keep the tools you used, choose individually, or let them lock. Nothing extra is charged today.
          </DialogDescription>

          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E0A08A] flex items-center gap-2">
            <Clock size={12} weight="duotone" /> Last day
          </div>
          <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight mt-1">
            You've built real things this week.
          </h3>
          <p className="text-sm text-[#E5E5E0]/90 mt-3">
            Tomorrow your trial becomes a {money(trial.subscription_cents)}/mo Creator account, and the
            tools you leaned on — <strong className="text-white">{toolList}</strong> — go quiet unless you
            keep them. What those tools BUILT stays in your silo either way, though Workbench and
            Refinery files need Minimum Creator Pro to stay openable. Keeping them is one
            charge, on tomorrow's invoice, nothing extra today.
          </p>

          <div className="mt-4">
            <TrialCreditNote trial={trial} tone="dark" testId="trial-lastcall-credit-note" />
          </div>

          <div className="mt-5 flex flex-col gap-2.5">
            <button
              onClick={keepEverything}
              disabled={busy || !trial.bundle_cents}
              data-testid="trial-lastcall-bundle"
              className="bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-5 py-3 rounded-md text-sm font-medium transition-colors disabled:opacity-60"
            >
              {busy ? "Adding…" : `Keep everything — ${money(trial.bundle_cents)}`}
            </button>
            <button
              onClick={() => { setOpen(false); setBuffet(true); }}
              disabled={busy}
              data-testid="trial-lastcall-pick"
              className="border border-white/25 text-[#FDFCF8] hover:bg-white/10 px-5 py-3 rounded-md text-sm transition-colors disabled:opacity-60"
            >
              Choose individually
            </button>
            <button
              onClick={close}
              disabled={busy}
              data-testid="trial-lastcall-decline"
              className="text-[#A8A8A0] hover:text-[#E5E5E0] px-5 py-2 rounded-md text-sm transition-colors disabled:opacity-60"
            >
              Let them lock
            </button>
          </div>

          {/* Separated from the consent block on purpose (§8.7). */}
          <div className="mt-5 pt-4 border-t border-white/10" data-testid="trial-lastcall-partner-note">
            <p className="text-[11px] italic text-[#E5E5E0]/70">
              Creator Pro owners can also join the Partner Program and earn referral commissions on
              software sales.
            </p>
            <EarningsDisclaimer variant="short" className="text-[#A8A8A0] mt-1" />
          </div>
        </DialogContent>
      </Dialog>

      <CreatorProBuffetModal
        open={buffet}
        onOpenChange={(v) => { setBuffet(v); if (!v) ackTrial("lastcall"); }}
        initialMode="customize"
      />
    </>
  );
}
